import { useState } from 'react'
import type { ReactElement } from 'react'

import { cn } from '../../lib/cn'
import { initials } from '../../lib/format'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg'

/**
 * The initials ramp is keyed to the box, not to the running type scale: each step is the largest
 * size two glyphs clear inside a 20/28/36/48 px circle. Three of the four land on a scale size, so
 * they read it from the token instead of re-typing the number — a change to `--t-small-size` now
 * reaches the avatar. They take the size only: the matching role classes would also impose their
 * line height, and `.t-label` would add uppercase and `0.08em` tracking to initials that are
 * already spaced by `initials()`. The 20 px box has no step on the scale; 0.5625rem is what fits,
 * and it stays a literal on purpose.
 */
const SIZES: Record<AvatarSize, { box: string; text: string; px: number }> = {
  xs: { box: 'h-5 w-5', text: 'text-[0.5625rem]', px: 20 },
  sm: { box: 'h-7 w-7', text: 'text-[length:var(--t-label-size)]', px: 28 },
  md: { box: 'h-9 w-9', text: 'text-[length:var(--t-small-size)]', px: 36 },
  lg: { box: 'h-12 w-12', text: 'text-[length:var(--t-h3-size)]', px: 48 },
}

export interface AvatarProps {
  name: string
  src?: string | null
  size?: AvatarSize
  /** Square with a small radius for brands, round for people. */
  shape?: 'circle' | 'square'
  /** Data colour drawn as a 2 px ring — a brand's identity colour, never chrome accent. */
  ringColor?: string
  surface?: 'paper' | 'chrome'
  className?: string
}

/** Falls back to initials when there is no image, and when the image fails to load. */
export const Avatar = ({
  name,
  src,
  size = 'md',
  shape = 'circle',
  ringColor,
  surface = 'paper',
  className,
}: AvatarProps): ReactElement => {
  const [failed, setFailed] = useState(false)
  const dims = SIZES[size]
  const showImage = Boolean(src) && !failed

  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden select-none',
        dims.box,
        shape === 'circle' ? 'rounded-full' : 'rounded-sm',
        surface === 'chrome' ? 'bg-chrome-700 text-chrome-mute' : 'bg-sunken text-ink-mute',
        className,
      )}
      style={
        // An outline rather than a box-shadow, so the ring reads on paper and on chrome alike.
        ringColor ? { outline: `2px solid ${ringColor}`, outlineOffset: '2px' } : undefined
      }
    >
      {showImage && src ? (
        <img
          src={src}
          alt=""
          width={dims.px}
          height={dims.px}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => {
            setFailed(true)
          }}
        />
      ) : (
        <span
          className={cn('font-semibold', dims.text)}
          style={{ fontVariationSettings: "'wdth' var(--wdth-label)" }}
          aria-hidden="true"
        >
          {initials(name)}
        </span>
      )}
    </span>
  )
}
